import jsPDF from "jspdf";
import type { EndorsementInput } from "@/types";
import { PAGE_W, MARGIN_X, CONTENT_W } from "./pdf-utils";
import { preprocessResumeText } from "./resume-preprocessor";

// ── shared regex patterns ──────────────────────────────────────────────────────

const BULLET_RE = /^\s*([•\-*➢✓→>·]\s+|\d+\.\s+|o\s+)/;
const DATE_ROW_RE = /^(.+?)\s*:\s*(.*\b(?:19|20)\d{2}\b.*)$/;
const JOB_ENTRY_RE = /\b(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:19|20)\d{2}\s*(?:[-–—]|\s+to)\s*(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:(?:19|20)\d{2}|present|current|now)\b/i;
const PURE_DATE_START_RE = /^(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:19|20)\d{2}\s*(?:[-–—]|\s+to)\s*(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:(?:19|20)\d{2}|present|current|now)\b/i;
const PERSONAL_DATA_LABEL_RE = /^(birth\s*date|date\s*of\s*birth|birthdate|dob|birth\s*place|place\s*of\s*birth|birthplace|age|height|weight|nationality|citizenship|civil\s*status|marital\s*status|religion|mother(?:'s|s)?\s*name|father(?:'s|s)?\s*name|mother|father|spouse(?:'s|s)?\s*name|spouse|gender|sex|full\s*name|name)$/i;
const WORK_EXP_RE = /^(working\s+experience|work\s+experience|professional\s+experience|employment\s+history|career\s+history|work\s+history)\s*:?\s*$/i;
const CONTINUATION_SUFFIX_RE = /(?:&|\b(?:and|or|but|nor|the|a|an|by|for|of|with|to|in|on|at|from|per|via|as|into|through|within|upon|about|after|before|against|between|among|toward|during|across|that|which|who|when|where|while|if))\s*$/i;
const MAIN_SECTION_RE = /^(professional\s+(?:work\s+)?(?:experience|summary)|work\s+experience|other\s+(?:work\s+)?experiences?|additional\s+(?:work\s+)?experiences?|employment\s+(?:history|record)|career\s+(?:summary|history|objective|profile)|education(?:al\s+(?:background|attainment|qualification))?|(?:seminar|training)s?\s*(?:attended|and\s+training)?|personal\s+(?:information|data|background|details?)|profile\s+summary|summary\s+(?:of\s+)?(?:qualifications?|experience|skills?)|objective)\s*:?\s*$/i;
const SUB_SECTION_RE = /^(skills?\s*(?:summary)?|core\s+competenc(?:y|ies)|technical\s+skills?|key\s+skills?|character\s+references?|references?|certifications?\s*(?:and\s+(?:awards?|honors?|recognition))?|awards?\s*(?:and\s+(?:certifications?|honors?|recognition))?|honors?\s*(?:and\s+awards?)?|contact\s+(?:information|details?)|languages?\s*(?:skills?)?|hobbies?\s*(?:and\s+interests?)?|interests?\s*(?:and\s+hobbies?)?|achievements?|accomplishments?|(?:professional\s+)?projects?|publications?|volunteer(?:ing)?\s+(?:experience|work)?|activities?)\s*:?\s*$/i;

const isMainHeader = (s: string, inWorkExpCtx = false): boolean => {
  if (JOB_ENTRY_RE.test(s)) return false;
  if (s.length <= 70 && MAIN_SECTION_RE.test(s)) return true;
  if (inWorkExpCtx) return false;
  const upper = (s.match(/[A-Z]/g) ?? []).length;
  return s.length <= 60 && upper >= 3 && !/[a-z]/.test(s);
};

const isSubHeader = (s: string): boolean => !JOB_ENTRY_RE.test(s) && s.length <= 70 && SUB_SECTION_RE.test(s);
const isHeaderLine = (s: string, inWorkExpCtx = false): boolean => isMainHeader(s, inWorkExpCtx) || isSubHeader(s);

// ── drawResumePages ────────────────────────────────────────────────────────────

export function drawResumePages(doc: jsPDF, input: EndorsementInput): void {
  const text = preprocessResumeText(input.resumeText || "", input.candidateName || "").trim();
  if (!text) return;

  doc.addPage();
  let y = 18;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text(input.candidateName || "Candidate", PAGE_W / 2, y + 6, { align: "center" });
  y += 14;
  doc.setLineWidth(0.4);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  y += 6;

  const bodyTop = y;
  const bodyLH = 4.2;
  const headerLH = 5.2;
  const bulletIndent = 5;
  const pageH = doc.internal.pageSize.getHeight();

  const needSpace = (h: number) => {
    if (y + h > pageH - 15) { doc.addPage(); y = 18; }
  };

  const renderBullet = (bodyText: string) => {
    const wrapped = doc.splitTextToSize(bodyText, CONTENT_W - bulletIndent);
    for (let i = 0; i < wrapped.length; i++) {
      needSpace(bodyLH);
      if (i === 0) doc.text("•", MARGIN_X, y);
      doc.text(wrapped[i], MARGIN_X + bulletIndent, y);
      y += bodyLH;
    }
  };

  // Pre-pass: merge hard-wrapped mid-sentence lines
  const rawLines = text.replace(/\r\n/g, "\n").split("\n");
  const lines: string[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    const stripped = rawLines[i].trim();
    if (!stripped || isHeaderLine(stripped)) { lines.push(rawLines[i]); continue; }
    let merged = stripped;
    while (i + 1 < rawLines.length) {
      const next = rawLines[i + 1].trim();
      if (!next || isHeaderLine(next)) break;
      if (/^\d{4}/.test(next) || PURE_DATE_START_RE.test(next) || JOB_ENTRY_RE.test(merged)) break;
      const nextStartsLower = /^[a-z]/.test(next);
      const prevEndsContinuation = CONTINUATION_SUFFIX_RE.test(merged);
      if (!nextStartsLower && !prevEndsContinuation) break;
      merged += " " + next;
      i++;
    }
    lines.push(merged);
  }

  let inWorkExp = false;
  let jobPhase: "header" | "after_company" | "description" = "header";
  let prevWasBlank = false;

  for (let li = 0; li < lines.length; li++) {
    const stripped = lines[li].trim();

    if (!stripped) {
      if (!prevWasBlank) y += 2;
      prevWasBlank = true;
      if (inWorkExp) jobPhase = "header";
      continue;
    }
    prevWasBlank = false;

    if (isMainHeader(stripped, inWorkExp)) {
      const isWorkExp = WORK_EXP_RE.test(stripped);
      needSpace(headerLH + 14);
      if (y > bodyTop) y += 4;
      inWorkExp = isWorkExp;
      jobPhase = "header";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(stripped, MARGIN_X, y);
      y += headerLH + 2;
      doc.setLineWidth(0.3);
      doc.setDrawColor(160, 160, 160);
      doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
      doc.setDrawColor(0, 0, 0);
      y += 3;
      continue;
    }

    if (isSubHeader(stripped)) {
      needSpace(headerLH + 8);
      if (y > bodyTop) y += 5;
      inWorkExp = false;
      jobPhase = "header";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.text(stripped, MARGIN_X, y);
      y += headerLH + 1;
      continue;
    }

    const bulletMatch = stripped.match(BULLET_RE);
    if (bulletMatch) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      renderBullet(stripped.slice(bulletMatch[0].length).trim());
      continue;
    }

    if (inWorkExp && jobPhase === "after_company") {
      if (/^\([^)]+\)$/.test(stripped)) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        needSpace(bodyLH);
        doc.text(stripped, MARGIN_X, y);
        y += bodyLH;
        continue;
      }
      const isDescVerbStart = /^(?:Lead|Manage|Develop|Oversee|Drive|Handle|Direct|Coordinate|Build|Responsible|Head|Create|Launch|Oversaw|Execute|Design|Establish|Implement|Deliver|Support|Maintain|Ensure|Collaborate|Supervise|Monitor|Analyze|Serve|Assist|Provide|Spearhead|Streamline|Facilitate|Conduct|Perform|Resolve|Administer|Prepare|Review|Evaluate|Train|Coach|Mentor)\b/i.test(stripped);
      if (isDescVerbStart) { jobPhase = "description"; renderBullet(stripped); continue; }
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9.5);
      needSpace(bodyLH);
      doc.text(stripped, MARGIN_X, y);
      y += bodyLH;
      jobPhase = "description";
      continue;
    }

    if (inWorkExp && jobPhase === "header" && !isHeaderLine(stripped, inWorkExp) && !bulletMatch && !DATE_ROW_RE.test(stripped) && !JOB_ENTRY_RE.test(stripped)) {
      let pureDateIdx = -1;
      for (let k = li + 1; k < Math.min(li + 4, lines.length); k++) {
        const pk = lines[k].trim();
        if (!pk) break;
        if (isHeaderLine(pk, inWorkExp) || BULLET_RE.test(pk)) break;
        if (PURE_DATE_START_RE.test(pk)) { pureDateIdx = k; break; }
      }
      if (pureDateIdx >= 0) {
        const entryLines: string[] = [stripped];
        for (let k = li + 1; k < pureDateIdx; k++) {
          const t = lines[k].trim();
          if (t) entryLines.push(t);
        }
        const dateStr = lines[pureDateIdx].trim();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        needSpace(bodyLH);
        doc.text(entryLines[0], MARGIN_X, y);
        y += bodyLH;
        doc.setFont("helvetica", "normal");
        for (let h = 1; h < entryLines.length; h++) {
          needSpace(bodyLH);
          doc.text(entryLines[h], MARGIN_X, y);
          if (h === entryLines.length - 1) doc.text(dateStr, PAGE_W - MARGIN_X, y, { align: "right" });
          y += bodyLH;
        }
        if (entryLines.length === 1) { needSpace(bodyLH); doc.text(dateStr, PAGE_W - MARGIN_X, y, { align: "right" }); y += bodyLH; }
        inWorkExp = true;
        jobPhase = entryLines.length > 1 ? "description" : "after_company";
        li = pureDateIdx;
        continue;
      }
    }

    if (JOB_ENTRY_RE.test(stripped) && !DATE_ROW_RE.test(stripped)) {
      const match = stripped.match(/^(.*?)\s*\b((?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:19|20)\d{2}\s*(?:[-–—]|\s+to)\s*(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+)?(?:(?:19|20)\d{2}|present|current|now)\b.*)\s*$/i);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      needSpace(bodyLH);
      if (match && match[1].trim()) {
        doc.text(match[1].trim(), MARGIN_X, y);
        doc.text(match[2].trim(), PAGE_W - MARGIN_X, y, { align: "right" });
      } else {
        doc.text(stripped, PAGE_W - MARGIN_X, y, { align: "right" });
      }
      y += bodyLH;
      inWorkExp = true;
      jobPhase = match && match[1].trim() ? "after_company" : "description";
      continue;
    }

    const dateMatch = stripped.match(DATE_ROW_RE);
    if (dateMatch && !PERSONAL_DATA_LABEL_RE.test(dateMatch[1])) {
      const left = dateMatch[1].trim();
      const right = dateMatch[2].trim();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      const rw = doc.getTextWidth(right);
      const lw = doc.getTextWidth(left);
      if (rw + lw + 4 <= CONTENT_W) { needSpace(bodyLH); doc.text(left, MARGIN_X, y); doc.text(right, PAGE_W - MARGIN_X, y, { align: "right" }); y += bodyLH; }
      else { const wrapped = doc.splitTextToSize(`${left}: ${right}`, CONTENT_W); for (const w of wrapped) { needSpace(bodyLH); doc.text(w, MARGIN_X, y); y += bodyLH; } }
      if (inWorkExp) jobPhase = "description";
      continue;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    if (inWorkExp && jobPhase === "description") {
      if (!BULLET_RE.test(stripped) && !/[a-z]/.test(stripped) && /[A-Z]/.test(stripped) && stripped.length >= 2 && stripped.length <= 80) {
        let hasDateAhead = false;
        for (let k = li + 1; k < Math.min(li + 4, lines.length); k++) {
          const pk = lines[k].trim();
          if (!pk) break;
          if (PURE_DATE_START_RE.test(pk) || JOB_ENTRY_RE.test(pk)) { hasDateAhead = true; break; }
        }
        if (hasDateAhead) {
          needSpace(headerLH + 8);
          if (y > bodyTop) y += 3;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9.5);
          doc.text(stripped, MARGIN_X, y);
          y += bodyLH;
          jobPhase = "after_company";
          continue;
        }
      }
      renderBullet(stripped);
    } else {
      const wrapped = doc.splitTextToSize(stripped, CONTENT_W);
      for (const w of wrapped) { needSpace(bodyLH); doc.text(w, MARGIN_X, y); y += bodyLH; }
    }
  }
}

// ── drawResumePagesMd ──────────────────────────────────────────────────────────

export function drawResumePagesMd(doc: jsPDF, input: EndorsementInput): void {
  const text = (input.resumeText || "").trim();
  if (!text) return;

  doc.addPage();
  let y = 18;
  const pageH = doc.internal.pageSize.getHeight();
  const bodyLH = 4.4;
  const INDENT_H3 = MARGIN_X + 2;
  const INDENT_BULLET = MARGIN_X + 5;
  const INDENT_SUBBULLET = MARGIN_X + 9;
  const BULLET_CONTENT_W = CONTENT_W - 5;
  const SUBBULLET_CONTENT_W = CONTENT_W - 9;

  const needSpace = (h: number) => {
    if (y + h > pageH - 15) { doc.addPage(); y = 18; }
  };

  let nameHeaderDone = false;
  for (const raw of text.split("\n")) {
    if (raw.startsWith("# ")) {
      if (!nameHeaderDone) {
        const name = raw.slice(2).trim();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(0, 0, 0);
        doc.text(name, PAGE_W / 2, y + 6, { align: "center" });
        y += 14;
        doc.setLineWidth(0.4);
        doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
        y += 6;
        nameHeaderDone = true;
      }
      continue;
    }

    if (raw.startsWith("## ")) {
      const title = raw.slice(3).trim().toUpperCase();
      needSpace(14);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(34, 34, 34);
      doc.text(title, MARGIN_X, y);
      y += 2;
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
      doc.setDrawColor(0);
      y += 4;
      continue;
    }

    if (raw.startsWith("### ")) {
      const title = raw.slice(4).trim();
      needSpace(bodyLH + 3);
      y += 2;
      const wrapped = doc.splitTextToSize(title, CONTENT_W - 2);
      for (const w of wrapped) {
        needSpace(bodyLH);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.setTextColor(34, 34, 34);
        doc.text(w, INDENT_H3, y);
        y += bodyLH + 0.5;
      }
      continue;
    }

    if (/^  +- /.test(raw)) {
      const btext = raw.replace(/^\s+- /, "").trim();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(34, 34, 34);
      const wrapped = doc.splitTextToSize(btext, SUBBULLET_CONTENT_W);
      for (let i = 0; i < wrapped.length; i++) {
        needSpace(bodyLH);
        if (i === 0) doc.text("◦", INDENT_BULLET + 1, y);
        doc.text(wrapped[i], INDENT_SUBBULLET, y);
        y += bodyLH;
      }
      continue;
    }

    if (raw.startsWith("- ")) {
      const btext = raw.slice(2).trim();
      if (/^(?:Phone|Mobile|Cell(?:phone)?|Tel(?:ephone)?|Contact\s*(?:No\.?|Number)?):/i.test(btext)) continue;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(34, 34, 34);
      const wrapped = doc.splitTextToSize(btext, BULLET_CONTENT_W);
      for (let i = 0; i < wrapped.length; i++) {
        needSpace(bodyLH);
        if (i === 0) doc.text("•", MARGIN_X + 1, y);
        doc.text(wrapped[i], INDENT_BULLET, y);
        y += bodyLH;
      }
      continue;
    }

    const stripped = raw.trim();
    if (!stripped) { y += 2; continue; }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(34, 34, 34);
    const wrapped = doc.splitTextToSize(stripped, CONTENT_W);
    for (const w of wrapped) { needSpace(bodyLH); doc.text(w, MARGIN_X, y); y += bodyLH; }
  }
}
